import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
} from '@mui/material';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Task as TaskIcon,
  Warning as WarningIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as EventIcon,
  DonutLarge as DonutLargeIcon,
  TodayOutlined as TodayIcon,
  HistoryOutlined as HistoryIcon,
  SpaceDashboardOutlined as DashboardIcon,
  InboxOutlined as InboxIcon,
} from '@mui/icons-material';
import PageHeader from '../components/layout/PageHeader';
import SectionTitle from '../components/layout/SectionTitle';
import { useNavigate } from 'react-router-dom';
import { reportApi, activityApi, taskApi } from '../api/client';
import { DashboardStats, Activity, Task } from '../types';
import { statusColors, statusLabels, activityTypeLabels } from '../theme';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color?: string;
  tint?: string;
  subtitle?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color = '#208040',
  tint = '#EAF4ED',
  subtitle,
  onClick,
}) => (
  <Card
    onClick={onClick}
    sx={{
      position: 'relative',
      overflow: 'hidden',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 120ms ease, box-shadow 120ms ease',
      ...(onClick ? {
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 8px rgba(32,128,64,0.12)',
        },
      } : {}),
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        backgroundColor: color,
      },
    }}
  >
    <CardContent sx={{ p: '12px 14px 12px 16px !important' }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Montserrat", sans-serif',
              fontSize: '1.75rem',
              fontWeight: 700,
              lineHeight: 1.1,
              color: 'text.primary',
              mt: 0.5,
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              sx={{ fontSize: '0.6875rem', fontWeight: 600, color, mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            bgcolor: tint,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'CALL':
      return <PhoneIcon />;
    case 'EMAIL':
      return <EmailIcon />;
    case 'MEETING':
      return <EventIcon />;
    default:
      return <PeopleIcon />;
  }
};

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [todayTasks, setTodayTasks] = useState<Task[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, activitiesRes, todayRes, overdueRes] = await Promise.all([
          reportApi.getDashboard(),
          activityApi.getRecent(10),
          taskApi.getToday(),
          taskApi.getOverdue(),
        ]);

        setStats(statsRes.data.data);
        setRecentActivities(activitiesRes.data.data);
        setTodayTasks(todayRes.data.data);
        setOverdueTasks(overdueRes.data.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const subtitleText = loading
    ? 'Henter data…'
    : `${stats?.totalCustomers ?? 0} kunder · ${stats?.recentLeads ?? 0} nye leads · ${todayTasks.length} opgaver i dag`;

  if (loading) {
    return (
      <Box>
        <PageHeader
          title="Dashboard"
          subtitle={subtitleText}
          icon={<DashboardIcon sx={{ fontSize: 18 }} />}
        />
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
              <Skeleton variant="rounded" height={84} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={120} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rounded" height={120} />
          </Grid>
        </Grid>
        <Skeleton variant="rounded" height={220} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle={subtitleText}
        icon={<DashboardIcon sx={{ fontSize: 18 }} />}
      />

      {/* Stats Cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Totale kunder"
            value={stats?.totalCustomers || 0}
            icon={<PeopleIcon sx={{ fontSize: 18 }} />}
            color="#208040"
            tint="#EAF4ED"
            onClick={() => navigate('/customers')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Nye leads (30d)"
            value={stats?.recentLeads || 0}
            icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
            color="#E8A33D"
            tint="#FBF1DE"
            onClick={() => navigate('/customers?status=LEAD')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Aktive (måned)"
            value={stats?.activeThisMonth || 0}
            icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
            color="#16602F"
            tint="#DDEEE2"
            onClick={() => navigate('/customers?status=ACTIVE')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Forfaldne"
            value={overdueTasks.length}
            icon={<WarningIcon sx={{ fontSize: 18 }} />}
            color={overdueTasks.length > 0 ? '#C74A3E' : '#9CA9A1'}
            tint={overdueTasks.length > 0 ? '#FBEAE8' : '#EDF0EE'}
            onClick={() => navigate('/tasks')}
          />
        </Grid>
      </Grid>

      {/* Status Overview */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent sx={{ p: '10px 12px !important' }}>
              <SectionTitle icon={<DonutLargeIcon sx={{ fontSize: 16 }} />}>
                Kunder efter status
              </SectionTitle>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {stats?.customersByStatus &&
                  Object.entries(stats.customersByStatus).map(([status, count]) => (
                    <Chip
                      key={status}
                      label={`${statusLabels[status] || status}: ${count}`}
                      size="small"
                      sx={{
                        bgcolor: statusColors[status] || '#9e9e9e',
                        color: 'white',
                        fontSize: '0.625rem',
                        height: 20,
                      }}
                    />
                  ))}
                {(!stats?.customersByStatus ||
                  Object.keys(stats.customersByStatus).length === 0) && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                      py: 2,
                      color: 'text.secondary',
                    }}
                  >
                    <InboxIcon sx={{ fontSize: 28, color: '#60A080', mb: 0.5 }} />
                    <Typography variant="caption">Ingen kunder endnu</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent sx={{ p: '10px 12px !important' }}>
              <SectionTitle
                icon={<TodayIcon sx={{ fontSize: 16 }} />}
                action={
                  <Button
                    size="small"
                    variant="text"
                    sx={{ minWidth: 'auto', p: '2px 6px', fontSize: '0.65rem' }}
                    onClick={() => navigate('/tasks')}
                  >
                    Se alle
                  </Button>
                }
              >
                Dagens opgaver
              </SectionTitle>
              {todayTasks.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 2,
                    color: 'text.secondary',
                  }}
                >
                  <TodayIcon sx={{ fontSize: 28, color: '#60A080', mb: 0.5 }} />
                  <Typography variant="caption">Ingen opgaver i dag</Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {todayTasks.slice(0, 4).map((task) => (
                    <ListItem key={task.id} sx={{ px: 0, py: 0.25 }}>
                      <ListItemAvatar sx={{ minWidth: 28 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 22, height: 22 }}>
                          <TaskIcon sx={{ fontSize: '0.75rem' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={task.title}
                        secondary={task.customer?.name}
                        primaryTypographyProps={{ fontSize: '0.75rem', fontWeight: 500 }}
                        secondaryTypographyProps={{ fontSize: '0.65rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activities */}
      <Card>
        <CardContent sx={{ p: '10px 12px !important' }}>
          <SectionTitle
            icon={<HistoryIcon sx={{ fontSize: 16 }} />}
            action={
              <Button
                size="small"
                variant="text"
                sx={{ minWidth: 'auto', p: '2px 6px', fontSize: '0.65rem' }}
                onClick={() => navigate('/customers')}
              >
                Se alle kunder
              </Button>
            }
          >
            Seneste aktiviteter
          </SectionTitle>
          {recentActivities.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 2,
                color: 'text.secondary',
              }}
            >
              <HistoryIcon sx={{ fontSize: 28, color: '#60A080', mb: 0.5 }} />
              <Typography variant="caption">Ingen aktiviteter endnu</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {recentActivities.slice(0, 6).map((activity) => (
                <ListItem
                  key={activity.id}
                  sx={{ px: 0, py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => navigate(`/customers/${activity.customerId}`)}
                >
                  <ListItemAvatar sx={{ minWidth: 32 }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 24, height: 24, '& svg': { fontSize: '0.85rem' } }}>
                      {getActivityIcon(activity.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>{activity.title}</Typography>
                        <Chip
                          label={activityTypeLabels[activity.type] || activity.type}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" component="span" sx={{ fontSize: '0.65rem' }}>
                        {activity.customer?.name} &bull;{' '}
                        {format(new Date(activity.date), 'PP', { locale: da })}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
